import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ReconcileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  DoneAll as ClearIcon,
  RemoveDone as UnclearIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import { formatAccountingAmount } from '../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import LedgerListPage from './LedgerListPage';
import LedgerFormDialog from './LedgerFormDialog';
import { AccountPicker } from './LedgerPickers';
import { useLedgerLookups } from './Journals';
import { ReconStatusChip, reconIdOf, reconItemIdOf, fmtDate, getErrorMessage, unwrap } from './recon/ReconShared';

const emptyForm = () => ({
  account_id: '',
  statement_date: new Date().toISOString().slice(0, 10),
  statement_balance: '',
  notes: '',
});

const emptyItem = () => ({ transaction_date: new Date().toISOString().slice(0, 10), description: '', debit_amount: '', credit_amount: '' });

// Bank Reconciliation (verified against BankReconciliationController): one
// reconciliation per (account x statement date). Items are statement/book
// lines - tick them cleared in bulk, add manual lines for outstanding
// cheques/deposits, then reconcile. Cleared items and reconciling lock the
// record (updates/deletes are blocked once reconciled).
const Recon = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.RECON, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.RECON, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.RECON, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.RECON, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showItem, setShowItem] = useState(false);
  const [itemValues, setItemValues] = useState(emptyItem());
  const [itemSaving, setItemSaving] = useState(false);

  const reload = () => setRefreshKey((k) => k + 1);

  const accountLabel = (r) =>
    r.account
      ? `${r.account.account_code} - ${r.account.account_name}`
      : look.accountLabel(r.account_id, r.account_id ? `Account ${r.account_id}` : '-');

  const submitCreate = async () => {
    if (!values.account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    if (!values.statement_date || values.statement_balance === '') {
      showSnackbar('Statement date and balance are required', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createBankReconciliation({
        account_id: Number(values.account_id),
        statement_date: values.statement_date,
        statement_balance: Number(values.statement_balance),
        ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
      });
      const created = unwrap(res);
      showSnackbar('Reconciliation opened', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
      if (created && (created.reconciliation_id || created.id)) openDetail(created);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getBankReconciliation(reconIdOf(row));
      setDetail(unwrap(res));
      setSelected([]);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch reconciliation'), 'error');
    }
  };

  const refreshDetail = async (id) => {
    try {
      const res = await ledgerAPI.getBankReconciliation(id);
      setDetail(unwrap(res));
      setSelected([]);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to refresh'), 'error');
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await ledgerAPI.updateBankReconciliation(reconIdOf(editing.src), editing.values);
      showSnackbar('Reconciliation updated', 'success');
      setEditing(null);
      reload();
      if (detail) refreshDetail(reconIdOf(detail));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn, after) => {
    confirm({
      title: label,
      message: label === 'Reconcile'
        ? 'Reconcile this statement? Uncleared items stay outstanding for the next statement.'
        : `Are you sure you want to ${label.toLowerCase()}?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(pastTense, 'success');
          after?.();
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const bulkClear = async (clear) => {
    if (!selected.length) {
      showSnackbar('Tick at least one item first', 'error');
      return;
    }
    try {
      if (clear) await ledgerAPI.clearReconItems(reconIdOf(detail), { item_ids: selected });
      else await ledgerAPI.unclearReconItems(reconIdOf(detail), { item_ids: selected });
      showSnackbar(`Item(s) marked as ${clear ? 'cleared' : 'uncleared'}`, 'success');
      refreshDetail(reconIdOf(detail));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitItem = async () => {
    if (!itemValues.transaction_date || !itemValues.description?.trim()) {
      showSnackbar('Transaction date and description are required', 'error');
      return;
    }
    if (!(Number(itemValues.debit_amount) > 0) && !(Number(itemValues.credit_amount) > 0)) {
      showSnackbar('Enter a debit or a credit amount', 'error');
      return;
    }
    try {
      setItemSaving(true);
      await ledgerAPI.addReconItem(reconIdOf(detail), {
        transaction_date: itemValues.transaction_date,
        description: itemValues.description.trim(),
        ...(Number(itemValues.debit_amount) > 0 ? { debit_amount: Number(itemValues.debit_amount) } : {}),
        ...(Number(itemValues.credit_amount) > 0 ? { credit_amount: Number(itemValues.credit_amount) } : {}),
      });
      showSnackbar('Item added', 'success');
      setShowItem(false);
      setItemValues(emptyItem());
      refreshDetail(reconIdOf(detail));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setItemSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const detailItems = Array.isArray(detail?.items) ? detail.items : [];
  const locked = Boolean(detail?.is_reconciled);

  if (!can(RESOURCES.LEDGER.RECON, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Bank Reconciliation.</Alert>;
  }

  return (
    <Box>
      <ModulePageHeader
        title="Bank Reconciliation"
        subtitle="Tie bank statements to the books - clear lines, explain the difference, reconcile"
      />
      <Alert severity="info" sx={{ mb: 3 }}>
        Before opening a reconciliation, the account must have reconciliation enabled on its chart-of-accounts
        record and be linked under Cash &amp; Bank -&gt; Setup -&gt; Reconciliation Accounts - otherwise the
        ledger rejects it with &quot;Account does not require reconciliation&quot;.
      </Alert>
      <LedgerListPage
        resource={RESOURCES.LEDGER.RECON}
        title="Reconciliations"
        subtitle="One reconciliation per account and statement date"
        addLabel="New Reconciliation"
        idKey="reconciliation_id"
        searchPlaceholder="Search reconciliations..."
        emptyMessage="No reconciliations found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getBankReconciliations(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { type: 'picker', key: 'account_id', label: 'Account', picker: AccountPicker },
          { key: 'is_reconciled', label: 'State', options: [{ label: 'Reconciled', value: true }, { label: 'Outstanding', value: false }] },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Statement date' },
        ]}
        columns={[
          { label: 'Account', render: (r) => <strong>{accountLabel(r)}</strong> },
          { label: 'Statement Date', render: (r) => fmtDate(r.statement_date) },
          { label: 'Statement', render: (r) => formatAccountingAmount(r.statement_balance), align: 'right', hideOnMobile: true },
          { label: 'Book', render: (r) => formatAccountingAmount(r.book_balance), align: 'right', hideOnMobile: true },
          { label: 'Difference', render: (r) => formatAccountingAmount(r.difference), align: 'right' },
          { label: 'State', render: (r) => <ReconStatusChip reconciled={r.is_reconciled} /> },
        ]}
        rowActions={(r) => {
          const id = reconIdOf(r);
          const actions = [{ icon: ViewIcon, title: 'Open workspace', color: 'default', onClick: () => openDetail(r) }];
          if (!r.is_reconciled && canUpdate) {
            actions.push({
              icon: EditIcon, title: 'Edit', color: 'primary',
              onClick: () => setEditing({ src: r, values: { statement_date: r.statement_date?.slice(0, 10) || '', statement_balance: r.statement_balance ?? '', notes: r.notes || '' } }),
            });
          }
          if (!r.is_reconciled && canApprove) {
            actions.push({ icon: ReconcileIcon, title: 'Reconcile', color: 'primary', onClick: () => runWorkflow('Reconcile', 'Reconciled successfully', () => ledgerAPI.reconcileBankAccount(id), () => openDetail(r)) });
          }
          if (!r.is_reconciled && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete', color: 'error',
              onClick: () => confirm({
                title: 'Delete reconciliation',
                message: 'Delete this unreconciled statement? Its items go with it.',
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteBankReconciliation(id);
                    showSnackbar('Reconciliation deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Reconciliation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <AccountPicker label="Bank/cash account" value={values.account_id} onChange={(id) => setValues((p) => ({ ...p, account_id: id }))} required />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Statement Date" value={values.statement_date}
                onChange={(e) => setValues((p) => ({ ...p, statement_date: e.target.value }))} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Statement Balance" value={values.statement_balance}
                onChange={(e) => setValues((p) => ({ ...p, statement_balance: e.target.value }))} inputProps={{ step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Notes" value={values.notes} onChange={(e) => setValues((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Open Reconciliation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail ? `${accountLabel(detail)} - ${fmtDate(detail.statement_date)}` : ''}
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box display="flex" flexWrap="wrap" gap={3} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2">Statement: <strong>{formatAccountingAmount(detail.statement_balance)}</strong></Typography>
                <Typography variant="body2">Book: <strong>{formatAccountingAmount(detail.book_balance)}</strong></Typography>
                <Typography variant="body2">Difference: <strong>{formatAccountingAmount(detail.difference)}</strong></Typography>
                <ReconStatusChip reconciled={detail.is_reconciled} />
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                {!locked && canUpdate && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setItemValues(emptyItem()); setShowItem(true); }}>
                    Add Item
                  </Button>
                )}
                {!locked && canApprove && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<ClearIcon />} onClick={() => bulkClear(true)} disabled={!selected.length}>
                      Mark Cleared ({selected.length})
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<UnclearIcon />} onClick={() => bulkClear(false)} disabled={!selected.length}>
                      Mark Uncleared
                    </Button>
                  </>
                )}
                {!locked && canApprove && (
                  <Button size="small" variant="contained" startIcon={<ReconcileIcon />} onClick={() => runWorkflow('Reconcile', 'Reconciled successfully', () => ledgerAPI.reconcileBankAccount(reconIdOf(detail)), () => refreshDetail(reconIdOf(detail)))}>
                    Reconcile
                  </Button>
                )}
              </Box>
              <DataTable
                columns={['', 'Date', 'Description', 'Debit', 'Credit', 'Cleared', '']}
                data={detailItems}
                loading={false}
                emptyMessage="No items yet - add statement lines to start ticking."
                renderRow={(it) => {
                  const iid = reconItemIdOf(it);
                  const cleared = Boolean(it.is_cleared ?? it.cleared);
                  return (
                    <TableRow key={iid}>
                      <TableCell padding="checkbox">
                        {!locked && canApprove && (
                          <Checkbox size="small" checked={selected.includes(iid)} onChange={() => toggleSelect(iid)} />
                        )}
                      </TableCell>
                      <TableCell>{fmtDate(it.transaction_date)}</TableCell>
                      <TableCell>{it.description}</TableCell>
                      <TableCell align="right">{formatAccountingAmount(it.debit_amount)}</TableCell>
                      <TableCell align="right">{formatAccountingAmount(it.credit_amount)}</TableCell>
                      <TableCell>{cleared ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {!locked && !cleared && canDelete && (
                          <IconButton size="small" color="error" title="Delete item" onClick={() => confirm({
                            title: 'Delete item',
                            message: 'Delete this reconciliation item?',
                            onConfirm: async () => {
                              try {
                                await ledgerAPI.deleteReconItem(reconIdOf(detail), iid);
                                showSnackbar('Item deleted', 'success');
                                refreshDetail(reconIdOf(detail));
                              } catch (error) {
                                showSnackbar(getErrorMessage(error), 'error');
                              }
                            },
                          })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }}
              />
              {detail.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{detail.notes}</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showItem} onClose={() => setShowItem(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Reconciliation Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Transaction Date" value={itemValues.transaction_date}
                onChange={(e) => setItemValues((p) => ({ ...p, transaction_date: e.target.value }))} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Description" value={itemValues.description} onChange={(e) => setItemValues((p) => ({ ...p, description: e.target.value }))} required />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Debit Amount" value={itemValues.debit_amount}
                onChange={(e) => setItemValues((p) => ({ ...p, debit_amount: e.target.value }))} inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Credit Amount" value={itemValues.credit_amount}
                onChange={(e) => setItemValues((p) => ({ ...p, credit_amount: e.target.value }))} inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowItem(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitItem} disabled={itemSaving}>
            {itemSaving ? 'Saving...' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit reconciliation"
        onSubmit={submitEdit}
        values={editing?.values || {}}
        onChange={(key, value) => setEditing((p) => (p ? { ...p, values: { ...p.values, [key]: value } } : p))}
        maxWidth="sm"
        fields={[
          { key: 'statement_date', label: 'Statement Date', type: 'date', md: 6 },
          { key: 'statement_balance', label: 'Statement Balance', type: 'number', md: 6 },
          { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
        ]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default Recon;